import React, { createContext, useContext, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import cx from 'classnames';
import { PlacesType } from 'react-tooltip';
import { IsTruncatedOptions, useIsTruncated } from '@/hooks/useIsTruncated';
import TooltipWrapper from '@/components/base/TooltipWrapper/TooltipWrapper';
import styles from './TooltipGroup.module.scss';

type TooltipGroupProps = {
  /** To define group boundaries */
  id: string;
  /** If explicitly set to "false", would render tooltips for each element, wrapped with Anchor*/
  shouldShowForTruncatedOnly?: boolean;
  rootClassName?: string;
  /** ClassName to customise current group's tooltip container appearance */
  tooltipClassName?: string;
  childrenWrapperClassName?: string;
  /** Delay before showing tooltip in ms */
  delayShow?: number;
  /** Renders the tooltip in `document.body` on the app's topmost layer, so no header, overlay, stacking context or overflow can cover or clip it */
  renderTooltipOnTop?: boolean;
  children: React.ReactNode;
};

const FLOATING_TOOLTIP_Z_INDEX = 'var(--z-floating)';
// Above --z-above-all, its `+ 1` tooltips and the hardcoded 10000 layers; zIndexScale.test.ts keeps it the highest
const TOP_TOOLTIP_Z_INDEX = 'calc(var(--z-above-all) + 2)';

export type TooltipProps = {
  isMultiLine?: boolean;
  value: string;
  children: React.ReactNode | ((state: { isTruncated: boolean }) => React.ReactNode);
  isTruncatedOptions?: IsTruncatedOptions;
  /** Specify position of tooltip as per react-tooltip documentation */
  place?: PlacesType;
  /** Specify unique className for tooltip, which would be applied exactly for this AnchorGroup, extending tooltipClassname from TooltipGroup */
  className?: string;
  // This is needed for icon tooltip. Because icon is not a text, there would be no tooltip.
  forceDisplayTooltip?: boolean;
};

type TooltipGroupContextValue = {
  id?: string;
  shouldShowForTruncatedOnly: boolean;
  hasProvider: boolean;
};

const DEFAULT_TOOLTIP_GROUP_CONTEXT: TooltipGroupContextValue = {
  id: undefined,
  shouldShowForTruncatedOnly: true,
  hasProvider: false,
};

const TooltipGroupContext = createContext<TooltipGroupContextValue>(DEFAULT_TOOLTIP_GROUP_CONTEXT);

export const useTooltipGroupContext = () => {
  return useContext(TooltipGroupContext);
};

export const TooltipGroup: React.FC<TooltipGroupProps> = ({
  id,
  shouldShowForTruncatedOnly = true,
  rootClassName,
  tooltipClassName,
  childrenWrapperClassName,
  delayShow = 900,
  renderTooltipOnTop = false,
  children,
}) => {
  const contextValue = useMemo(
    () => ({
      id: `${id}__tooltip`,
      shouldShowForTruncatedOnly,
      hasProvider: true,
    }),
    [id, shouldShowForTruncatedOnly],
  );

  const customStyles = useMemo<React.CSSProperties>(
    () => ({
      zIndex: renderTooltipOnTop ? TOP_TOOLTIP_Z_INDEX : FLOATING_TOOLTIP_Z_INDEX,
      // `.tooltipWrapper` also sets `position: relative`; inline keeps a body-level tooltip anchored whatever the stylesheet order
      ...(renderTooltipOnTop && { position: 'absolute' }),
    }),
    [renderTooltipOnTop],
  );

  const combinedTooltipClassName = cx(styles.tooltipWrapper, tooltipClassName);

  const tooltip = (
    <TooltipWrapper
      id={contextValue.id}
      place={'top'}
      className={combinedTooltipClassName}
      delayHide={200}
      delayShow={delayShow}
      style={customStyles}
      offset={4}
      clickable
    />
  );

  return (
    <TooltipGroupContext.Provider value={contextValue}>
      <div className={cx(styles.root, 'global-class', rootClassName)}>
        {renderTooltipOnTop ? (
          createPortal(tooltip, document.body)
        ) : (
          <div className={styles.tooltipWrapper}>{tooltip}</div>
        )}
        <div className={cx(styles.childrenWrapper, 'global-class', childrenWrapperClassName)}>{children}</div>
      </div>
    </TooltipGroupContext.Provider>
  );
};

export const TooltipGroupAnchor: React.FC<TooltipProps> = ({
  value,
  children,
  isTruncatedOptions,
  place,
  className,
  forceDisplayTooltip,
  isMultiLine,
}) => {
  const ref = useRef(null);

  const isTruncated = useIsTruncated(ref, isTruncatedOptions);
  const tooltipGroupContext = useTooltipGroupContext();
  const { id, shouldShowForTruncatedOnly } = tooltipGroupContext;
  const isHidden = shouldShowForTruncatedOnly ? !isTruncated : false;
  const renderedChildren = typeof children === 'function' ? children({ isTruncated }) : children;

  return (
    <div
      ref={ref}
      className={styles.tooltipAnchorWrapper}
      data-tooltip-id={id}
      data-tooltip-html={value}
      data-tooltip-hidden={forceDisplayTooltip ? false : isHidden}
      data-is-multiline={isMultiLine}
      {...(place && { ['data-tooltip-place']: place })}
      {...(className && { ['data-tooltip-class-name']: className })}
    >
      {renderedChildren}
    </div>
  );
};
